import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Frequency, Options, RRule, Weekday } from 'rrule';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FacadeService } from 'src/app/core/services/facade.service';
import { DateUtils } from '../../utils/date-utils';

// export function toNativeDate(ngbDate: NgbDate): Date {
//     return new Date(Date.UTC(ngbDate.year, ngbDate.month - 1, ngbDate.day));
// }

@Component({
	selector: 'app-recurring-event-picker',
	templateUrl: './recurring-event-picker.component.html',
	styleUrls: ['./recurring-event-picker.component.scss']
})
export class RecurringEventPickerComponent implements OnInit, OnDestroy {
	Frequency = Frequency;
	recurringForm: FormGroup;
	hoveredDate: Date | null = null;
	dates: Date[] = [];
	datesRemovedByWeekendSwitch: Date[] = [];
	isWeekendActiveR = false;
	customization = false;
	@Input() startDate: Date;
	@Output() datesList = new EventEmitter<Date[]>();

	isMobile = false;

	datesForm = [];

	private today: Date;
	private weekdayMap = [
		RRule.MO,
		RRule.TU,
		RRule.WE,
		RRule.TH,
		RRule.FR,
		RRule.SA,
		RRule.SU
	];
	private destroy$ = new Subject<void>();

	get f(): any {
		return this.recurringForm.controls;
	}

	constructor(
		private fb: FormBuilder,
		private dateUtils: DateUtils,
		private facade: FacadeService // private calendar: NgbCalendar, // public formatter: NgbDateParserFormatter
	) {}

	ngOnInit(): void {
		this.today = new Date();
		if (this.startDate) {
			this.today = new Date(
				this.startDate.getFullYear(),
				this.startDate.getMonth(),
				this.startDate.getDate()
			);
		}
		this.datesForm = [this.startDate, this.startDate];

		this.initRecurringForm();
		this.subscribeToFormValue();
		this.datesList.emit(this.dates);

		this.isMobile = this.facade.mobileService.isMobile();
	}
	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	changeWeekendSwitch() {
		this.isWeekendActiveR = !this.isWeekendActiveR;
		if (!this.isWeekendActiveR) {
			this.datesRemovedByWeekendSwitch = [];
			this.dates = this.dates.filter((date) => {
				if (date.getDay() !== 0 && date.getDay() !== 6) {
					return date;
				} else {
					this.datesRemovedByWeekendSwitch.push(date);
				}
			});
		} else {
			this.dates = this.dates.concat(this.datesRemovedByWeekendSwitch);
			this.dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
		}
		let removingDoublonsDates: number[] = this.dates.map((date) =>
			date.getTime()
		);
		removingDoublonsDates = [...new Set(removingDoublonsDates)];
		this.dates = removingDoublonsDates.map((dateInMs) => new Date(dateInMs));
		this.datesList.emit(this.dates);
	}

	private initRecurringForm(): void {
		let endDate = new Date();
		endDate.setDate(this.today.getDate() + 7);
		this.recurringForm = this.fb.group({
			// startDate: [this.today, Validators.required],
			// endDate: [
			//     new Date(this.today.getDate() + 7),
			//     // this.calendar.getNext(this.today, 'd', 7),
			//     Validators.required,
			// ],
			frequency: [Frequency.DAILY],
			onWeekday: this.fb.array(
				[false, false, false, false, false, false, false].map((val) =>
					this.fb.control(val)
				)
			),
			onMonthday: [this.today],
			datesForm: [[this.today, endDate]]
		});
	}

	private subscribeToFormValue(): void {
		this.recurringForm.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				let dateS: Date = this.dateUtils.convertDateToUTCDate(
					value.datesForm[0] || this.today
				);
				let dateE: Date = this.dateUtils.convertDateToUTCDate(
					value.datesForm[1] || this.today
				);
				const options: Partial<Options> = {
					freq: value.frequency || Frequency.DAILY,
					dtstart: dateS,
					until: dateE,
					// dtstart: toNativeDate(value.startDate || this.today),
					// until: toNativeDate(value.endDate || this.today),
					byweekday:
						value.frequency === Frequency.WEEKLY
							? this.getWeekday(value.onWeekday)
							: null,
					bymonthday:
						value.frequency === Frequency.MONTHLY
							? (value.onMonthday && value.onMonthday.getDate()) ||
							  this.today.getDay()
							: null
				};
				const rule = new RRule(options);
				//Si l'on est en vue par jour l'on vérifie si un jour est selectionner sinon on reset les dates
				if (value.frequency === Frequency.WEEKLY) {
					if (value.onWeekday.some((e) => e)) {
						this.dates = rule.all();
					} else {
						this.dates = [];
					}
				} else {
					this.dates = rule.all();
				}
				if (!this.isWeekendActiveR) {
					if (value.frequency === Frequency.DAILY) {
						this.dates = this.dates.filter((date) => {
							if (date.getDay() !== 0 && date.getDay() !== 6) {
								return date;
							} else {
								this.datesRemovedByWeekendSwitch.push(date);
							}
						});
					}
				}
				this.datesList.emit(this.dates);
			});

		this.recurringForm.patchValue({
			// startDate: this.today,
			// endDate: new Date(this.today),
			datesForm: [this.today, new Date(this.today)],
			// endDate: this.calendar.getNext(this.today, 'd', 0),
			frequency: Frequency.DAILY
		});
	}

	private getWeekday(byWeekday: boolean[]): any {
		const result = byWeekday
			.map((v, i) => (v && this.weekdayMap[i]) || null)
			.filter((v) => !!v);
		return result.length ? result : null;
	}

	supprimer(date: Date) {
		this.customization = true;
		const index = this.dates.indexOf(date);
		if (index !== -1) {
			this.dates.splice(index, 1);
		}
	}

	personalize() {
		this.customization = !this.customization;
	}
	generaliser() {
		this.customization = false;
	}
}
